import { useEffect, type ReactElement } from 'react';

/**
 * Themed confirm dialog (replaces window.confirm). A small modal with a title, body, and a
 * cancel/confirm pair. `danger` paints the confirm button red for destructive actions (e.g.
 * disconnecting an account). Escape and a backdrop click both cancel; the confirm button autofocuses.
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactElement | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div className="lapo-dialog-backdrop" onMouseDown={onCancel}>
      <div
        className="lapo-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        data-testid="confirm-dialog"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="lapo-dialog-title">{title}</h2>
        {body && <p className="lapo-dialog-body">{body}</p>}
        <div className="lapo-dialog-actions">
          <button type="button" className="lapo-btn" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`lapo-btn ${danger ? 'is-danger' : 'is-primary'}`}
            autoFocus
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
